import EventHandler from '@pixelunion/events';
import Disclosure from '@pixelunion/shopify-cross-border';

import NavDesktopMenu from '../components/NavDesktopMenu';
import NavMobileMenu from '../components/NavMobileMenu';

export default class Header {
  constructor(section) {
    this.el = section.el;
    this.data = section.data;
    this.events = new EventHandler();

    this.sticky = this.data.sticky_header;
    this.firstSectionIsBanner = false;
    this.transparent = false;

    this.main = document.querySelector('main');
    this.announcement = this.el.querySelector('[data-site-announcement]');
    this.utilityBar = this.el.querySelector('[data-header-utility]');
    this.header = this.el.querySelector('[data-site-header]');
    this.mobileControls = this.el.querySelector('[data-mobile-header-controls]');
    this.navigation = this.el.querySelector('[data-main-nav]');
    this.mobileNav = this.el.querySelector('[data-mobile-nav]');

    // We need to move the mobile nav in the dom to be outside of the header to allow for
    // appropriate z-index between the header controls, header, and mobile nav.
    this.mobileNav = document.body.insertBefore(this.mobileNav, this.el.nextSibling);

    this.navDesktopMenu = new NavDesktopMenu(this.navigation);
    this.navMobile = new NavMobileMenu({
      controls: this.mobileControls,
      mobileNav: this.mobileNav,
    });

    if (this.sticky) {
      document.body.classList.add('site-header--sticky');
    }

    if (this.data.transparent_header !== 'none') {
      // Determine if the first section is a banner which makes the header transparent
      const firstSection = this.main.querySelector('section');
      if (firstSection) {
        this.firstSectionIsBanner = firstSection.classList.contains('image-with-text-overlay--wide')
        || firstSection.classList.contains('article-template--width-wide')
        || firstSection.classList.contains('blog-template--width-wide')
        || firstSection.classList.contains('slideshow--width-wide')
        || firstSection.classList.contains('video-section--width-wide-true');
      }

      if (this.firstSectionIsBanner && (this.data.current_page === 'index' || this.data.transparent_header === 'site')) {
        this.main.classList.add('main--transparent-header');
      }

      this._setHeaderTransparency();

      // We only want to change transparency if the header is sticky
      if (this.sticky) {
        this.events.register(window, 'scroll', () => {
          this._setBodyScrolled();
          this._setHeaderTransparency();
        });
      }
    } else if (this.sticky) {
      this.events.register(window, 'scroll', () => {
        this._setBodyScrolled();
      });
    }

    this._setCSSHeightVariables();
    // If the window resizes, the number of rows of menu items might change, so we need to adjust
    // the variables that adjust the placement of the header and main elements.
    // This is also the case if the image logo size changes (usually editor only).
    this.events.register(window, 'resize', () => this._setCSSHeightVariables());
    this.events.register(this.header, 'rimg:load', () => this._setCSSHeightVariables());

    // Correcting for cached CSS variables that are making the screen not stop at position 0
    if (document.documentElement.scrollTop < 250) {
      window.scrollTo(0, 0);
    }

    // Currency and Language selectors
    this.currencyDisclosureEl = this.el.querySelector('[data-disclosure-currency]');
    this.localeDisclosureEl = this.el.querySelector('[data-disclosure-locale]');
    this.disclosures = [];

    if (this.currencyDisclosureEl) {
      this.disclosures.push(new Disclosure(this.currencyDisclosureEl));
    }

    if (this.localeDisclosureEl) {
      this.disclosures.push(new Disclosure(this.localeDisclosureEl));
    }

    this.el.classList.add('site-header__outer-wrapper--loaded');
  }

  _setBodyScrolled() {
    if (document.documentElement.scrollTop < 150) {
      document.body.classList.remove('body--scrolled');
    } else {
      document.body.classList.add('body--scrolled');
    }
  }

  _setHeaderTransparency() {
    // Sets the header transparent class based on the page and scroll position
    if (this.firstSectionIsBanner
      && (this.data.current_page === 'index' || this.data.transparent_header === 'site')
      && (!this.sticky || document.documentElement.scrollTop < 150)) {
      this.el.classList.add('site-header__outer-wrapper--transparent');

      if (!this.transparent) {
        this.transparent = true;
        this._setCSSHeightVariables();
      }
    } else {
      this.el.classList.remove('site-header__outer-wrapper--transparent');

      if (this.transparent) {
        this.transparent = false;
        this._setCSSHeightVariables();
      }
    }
  }

  _setCSSHeightVariables() {
    // Set CSS variables to adjust sticky header
    const announcementHeight = this.announcement ? this.announcement.offsetHeight : 0;
    const utilityHeight = this.utilityBar ? this.utilityBar.offsetHeight : 0;
    const headerHeight = this.header.offsetHeight;
    const fullHeaderHeight = utilityHeight + headerHeight;

    document.body.style.setProperty('--announcement-height', `${announcementHeight}px`);
    document.body.style.setProperty('--utility-height', `${utilityHeight}px`);
    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--full-header-height', `${fullHeaderHeight}px`);
  }

  onSectionUnload() {
    this.navDesktopMenu.unload();
    this.events.unregisterAll();
    this.disclosures.forEach(disclosure => disclosure.unload());

    document.body.classList.remove('site-header--sticky');
    this.main.classList.remove('main--transparent-header');
  }
}
